import { useState, useEffect, useCallback, useRef } from 'react';
import AuthService from '../services/AuthService';

/**
 * Custom hook for managing WebSocket connection to task status updates.
 *
 * Usage:
 * const { status, isConnected, subscribe, cancel, error } = useTaskStatus();
 *
 * // Subscribe to a task
 * subscribe('task_123456');
 *
 * // Cancel the task
 * cancel('User requested cancellation');
 */
export const useTaskStatus = () => {
  const [status, setStatus] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [subscribedTaskId, setSubscribedTaskId] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // FH-345: the app's own origin (the dev proxy and nginx forward /ws/task-status to the backend)
  const getWsUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/task-status`;
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = getWsUrl();
    console.log('Connecting to task status WebSocket:', wsUrl);

    try {
      // signed in only: a browser can't set an Authorization header on a WebSocket, so the token goes as a subprotocol
      const token = AuthService.getAccessToken();
      if (!token) return;
      const ws = new WebSocket(wsUrl, ['bearer', token]);

      ws.onopen = () => {
        console.log('Task status WebSocket connected');
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;

        // If we were subscribed to a task, re-subscribe
        if (subscribedTaskId) {
          ws.send(JSON.stringify({
            action: 'subscribe',
            taskId: subscribedTaskId
          }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Task status update:', data);

          if (data.action === 'error') {
            setError(data.message);
          } else if (data.action === 'subscribed') {
            console.log('Subscribed to task:', data.taskId);
          } else if (data.action === 'cancel_requested') {
            console.log('Cancel requested for task:', data.taskId);
          } else if (data.action === 'pong') {
            // Heartbeat response
          } else {
            // Status update
            setStatus(data);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onerror = (event) => {
        console.error('Task status WebSocket error:', event);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('Task status WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect if not a clean close
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('Error creating WebSocket:', e);
      setError('Failed to create WebSocket connection');
    }
  }, [getWsUrl, subscribedTaskId]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    setStatus(null);
    setSubscribedTaskId(null);
  }, []);

  // Subscribe to a task
  const subscribe = useCallback((taskId) => {
    if (!taskId) {
      setError('Task ID is required');
      return;
    }

    setSubscribedTaskId(taskId);
    setStatus(null);
    setError(null);

    // Connect if not connected
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connect();
    } else {
      // Send subscribe message
      wsRef.current.send(JSON.stringify({
        action: 'subscribe',
        taskId: taskId
      }));
    }
  }, [connect]);

  // Unsubscribe from task
  const unsubscribe = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'unsubscribe'
      }));
    }
    setSubscribedTaskId(null);
    setStatus(null);
  }, []);

  // Cancel the current task
  const cancel = useCallback((reason = 'Cancelled by user', cancelledBy = 'user') => {
    if (!subscribedTaskId) {
      setError('No task subscribed');
      return false;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket not connected');
      return false;
    }

    wsRef.current.send(JSON.stringify({
      action: 'cancel',
      taskId: subscribedTaskId,
      reason: reason,
      cancelledBy: cancelledBy
    }));

    return true;
  }, [subscribedTaskId]);

  // Send ping to keep connection alive
  const ping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'ping' }));
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Set up ping interval when connected
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(ping, 30000);
    return () => clearInterval(pingInterval);
  }, [isConnected, ping]);

  return {
    status,
    isConnected,
    error,
    subscribedTaskId,
    subscribe,
    unsubscribe,
    cancel,
    connect,
    disconnect
  };
};

export default useTaskStatus;
